import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Paper,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import MovieIcon from '@mui/icons-material/Movie';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import { useAppContext } from '../context/AppContext';
import { singingPhotoApi } from '../services/api';
import ImageUploader from '../components/ImageUploader';
import VideoPlayer from '../components/VideoPlayer';
import GenerationHistory from '../components/GenerationHistory';

// Utility function to handle both base64 and presigned URL formats
const processVideoDataUri = (data_uri: string): string => {
  // Check if it's already a URL (presigned URL)
  if (data_uri.startsWith('http://') || data_uri.startsWith('https://')) {
    return data_uri;
  }
  
  // Handle base64 format
  const clean = data_uri.startsWith('data:') ? data_uri.split(',')[1] : data_uri;
  return `data:video/mp4;base64,${clean}`;
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));
const PromptTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: theme.palette.divider },
    '&:hover fieldset': { borderColor: theme.palette.primary.main },
    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
  },
}));

const SingingPhotoGenerator: React.FC = () => {
  const { state, actions } = useAppContext();
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartTimeRef = useRef<number | null>(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [selectedQueue, setSelectedQueue] = useState<string>('singing-photo');

  const SINGING_PHOTO_QUEUES = [
    'singing-photo',
  ];

  const { errorMessage, generationProgress } = state;
  const { uploadedImage, isGenerating, generatedVideo } = state;
  const [duration, setDuration] = useState<number>(10);
  const [resolution, setResolution] = useState<number>(480);
  const [songStyle, setSongStyle] = useState<string>('');
  const [lyrics, setLyrics] = useState<string>('');

  useEffect(() => {
    return () => {
      // cleanup on unmount
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, []);

  const handleImageUpload = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        actions.setUploadedImage({ file, preview: String(reader.result) });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => actions.removeUploadedImage();
  const setErrorMessage = (message: string) => actions.setError(message);
  const handleSongStyleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSongStyle(e.target.value);
  const handleLyricsChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setLyrics(e.target.value);

  const handleReset = () => {
    actions.prepareNewGenerationNoHistory();
    setErrorMessage('');
    setStatusMessage('');
    setSongStyle('');
    setLyrics('');
  };

  const clearPolling = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const startProgressPolling = (taskId: string) => {
    setStatusMessage('Starting... grab a coffee, it usually takes ~3 minutes');
    const pollInterval = 10000;
    const maxPollTime = 600000;
    actions.updateGenerationProgress(0);
    pollStartTimeRef.current = Date.now();
    clearPolling();

    let currentIntervalId: any = null;
    let consecutiveErrors = 0;
    const maxConsecutiveErrors = 5;

    const pollForStatus = async () => {
      const currentTime = Date.now();
      const startTime = pollStartTimeRef.current ?? currentTime;
      const elapsedTime = currentTime - startTime;
      if (elapsedTime > maxPollTime) {
        if (currentIntervalId) {
          clearInterval(currentIntervalId);
          progressIntervalRef.current = null;
        }
        setErrorMessage('Task timeout - generation took too long');
        actions.prepareNewGenerationNoHistory();
        return;
      }
      try {
        const {
          status,
          progress: progressVal,
          statusMessages,
          queue_position,
        } = (await singingPhotoApi.checkStatus(taskId, selectedQueue)) as any;
        const progress = typeof progressVal === 'number' ? progressVal : undefined;
        consecutiveErrors = 0;
        if (typeof progress === 'number') actions.updateGenerationProgress(progress * 100);
        if (statusMessages)
          setStatusMessage(statusMessages.detail_message || statusMessages.message || '');
        else if (typeof progress === 'number')
          setStatusMessage(`Processing: ${Math.round(progress * 100)}%`);

        if (status && status.toLowerCase() === 'in_queue') {
          const tagline = queue_position ? `${queue_position} ahead of you.` : "You're next.";
          setStatusMessage(`One moment. We'll start generating shortly. ${tagline}`);
          return;
        }

        if (status && status.toLowerCase() === 'cancelled') {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          setStatusMessage('Video generation cancelled');
          setErrorMessage('Task was cancelled');
          actions.prepareNewGenerationNoHistory();
          return;
        }

        if (status && status.toLowerCase() === 'failed') {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          const failureReason = statusMessages?.message || statusMessages?.detail_message || 'Unknown error';
          setStatusMessage(`Video generation failed: ${failureReason}`);
          setErrorMessage(`Task failed: ${failureReason}. This may be due to image format, prompt complexity, or processing constraints. Please try with a different image or simpler prompt.`);
          actions.prepareNewGenerationNoHistory();
          return;
        }

        if (status && status.toLowerCase() === 'completed') {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          setStatusMessage('Singing photo video generation completed! Fetching result ...');

          const { video } = await singingPhotoApi.getResult(taskId, selectedQueue);
          const data_uri = video?.data_uri;

          if (video && data_uri) {
            const dataUriStr = data_uri as string;
            if (typeof dataUriStr !== 'string' || dataUriStr.length === 0) {
              setErrorMessage('Invalid video data received');
              actions.prepareNewGeneration();
              return;
            }

            setStatusMessage('Singing photo video generation completed.');
            const videoUrl = processVideoDataUri(dataUriStr);
            actions.completeGeneration(videoUrl);
          } else {
            setErrorMessage('Video generated but no data was provided');
            actions.prepareNewGeneration();
          }
        }
      } catch (err: any) {
        if (err.response && (err.response.status === 404 || err.response.status === 422)) {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          setErrorMessage(`Task error: ${err.response?.data?.detail || 'Unknown error'}`);
          actions.prepareNewGenerationNoHistory();
        } else {
          consecutiveErrors++;
          if (consecutiveErrors >= maxConsecutiveErrors) {
            if (currentIntervalId) {
              clearInterval(currentIntervalId);
              progressIntervalRef.current = null;
            }
            setErrorMessage(`Too many polling errors: ${err.message || 'Unknown error'}`);
            actions.prepareNewGenerationNoHistory();
          } else {
            setErrorMessage(
              `Polling error: ${err.message || 'Unknown error'} - Will retry... (${consecutiveErrors}/${maxConsecutiveErrors})`,
            );
          }
        }
      }
    };

    pollForStatus();
    const interval = setInterval(pollForStatus, pollInterval);
    currentIntervalId = interval;
    progressIntervalRef.current = interval;
    return interval;
  };

  const handleGenerate = async () => {
    if (!uploadedImage) {
      alert('Please upload an image');
      return;
    }
    setErrorMessage('');
    setStatusMessage('Submitting request...');
    try {
      const { request_id } = await singingPhotoApi.generateVideo(
        uploadedImage.file,
        songStyle,
        selectedQueue,
        duration,
        resolution,
        lyrics,
      );
      actions.startGeneration(request_id);
      startProgressPolling(request_id);
    } catch (error: any) {
      let errorDetail = 'Unknown error';
      if (error.response?.data?.detail) errorDetail = error.response.data.detail;
      else if (error.message) errorDetail = error.message;
      setErrorMessage(`Failed to start singing photo video generation: ${errorDetail}`);
      alert(`Error: ${errorDetail}`);
      actions.cancelGeneration();
    }
  };

  const handleUpscaleVideo = () => {
    if (generatedVideo) {
      actions.setUpscaleVideo(generatedVideo);
      actions.prepareNewGenerationNoHistory(); // Clear current video to avoid confusion
      actions.setActiveTab(2); // Switch to video upscale tab
    }
  };

  const handleSendToAddSound = () => {
    if (generatedVideo) {
      actions.setAddSoundVideo(generatedVideo);
      actions.prepareNewGenerationNoHistory(); // Clear current video to avoid confusion
      actions.setActiveTab(4); // Switch to Add Sound to Video tab
    }
  };

  return (
    <Grid container spacing={3} sx={{ height: 'calc(100vh - 120px)' }}>
      <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <StyledPaper elevation={3} sx={{ mb: 3, flex: '1 1 auto' }}>
          <Typography variant="h5" gutterBottom>
            Image Upload
          </Typography>
          <Box sx={{ mb: 3, flex: '0 0 auto' }}>
            <ImageUploader
              uploadedImage={uploadedImage as any}
              onUpload={handleImageUpload}
              onRemove={handleRemoveImage}
            />
          </Box>
          <Typography variant="h5" gutterBottom>
            Configuration
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel id="singing-photo-queue-label">Queue</InputLabel>
              <Select
                labelId="singing-photo-queue-label"
                id="singing-photo-queue"
                value={selectedQueue}
                label="Queue"
                disabled={isGenerating}
                onChange={(e) => setSelectedQueue(e.target.value as string)}
              >
                {SINGING_PHOTO_QUEUES.map((q) => (
                  <MenuItem key={q} value={q}>
                    {q}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ width: 140 }}>
              <InputLabel id="singing-photo-resolution-label">Resolution</InputLabel>
              <Select
                labelId="singing-photo-resolution-label"
                id="singing-photo-resolution"
                value={resolution}
                label="Resolution"
                disabled={isGenerating}
                onChange={(e) => setResolution(Number(e.target.value))}
              >
                <MenuItem value={480}>480p</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ width: 140 }}>
              <InputLabel id="singing-photo-duration-label">Duration (s)</InputLabel>
              <Select
                labelId="singing-photo-duration-label"
                id="singing-photo-duration"
                value={duration}
                label="Duration (s)"
                disabled={isGenerating}
                onChange={(e) => setDuration(Number(e.target.value))}
              >
                {[10, 20, 30].map((sec) => (
                  <MenuItem key={sec} value={sec}>
                    {sec}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <PromptTextField
            fullWidth
            variant="outlined"
            label="Song Style (optional)"
            placeholder="e.g., pop, rock, jazz, classical..."
            value={songStyle}
            onChange={handleSongStyleChange}
            disabled={isGenerating}
            sx={{ mb: 2 }}
          />
          <PromptTextField
            fullWidth
            multiline
            rows={5}
            variant="outlined"
            label="Lyrics (optional)"
            placeholder="Enter lyrics here..."
            value={lyrics}
            onChange={handleLyricsChange}
            disabled={isGenerating}
            sx={{ mb: 3 }}
          />
          <Box sx={{ display: 'flex', gap: 2, mt: 'auto' }}>
            {!generatedVideo ? (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={!uploadedImage || isGenerating}
                  onClick={handleGenerate}
                  startIcon={<MovieIcon />}
                  fullWidth
                >
                  Generate Singing Photo
                </Button>
                {isGenerating && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="large"
                    onClick={() => {
                      if (state.generationTaskId) {
                        clearPolling();
                        singingPhotoApi
                          .cancelTask(state.generationTaskId, selectedQueue)
                          .then(() => {
                            actions.prepareNewGenerationNoHistory();
                            setStatusMessage('Singing photo video generation cancelled');
                          })
                          .catch((err: any) => {
                            actions.prepareNewGenerationNoHistory();
                            setErrorMessage(
                              'Error cancelling task: ' + (err.message || 'Unknown error'),
                            );
                          });
                      }
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => {
                  clearPolling();
                  actions.prepareNewGenerationNoHistory();
                  handleGenerate();
                }}
                startIcon={<RefreshIcon />}
                fullWidth
              >
                New Generation
              </Button>
            )}
          </Box>
        </StyledPaper>
        <Box sx={{ flex: '0 0 auto', height: '250px' }}>
          <GenerationHistory />
        </Box>
      </Grid>
      <Grid item xs={12} md={6} sx={{ height: '100%' }}>
        <StyledPaper
          elevation={3}
          sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <Typography variant="h5" gutterBottom>
            Generated Singing Photo Video - Usually takes around 3 minutes
          </Typography>
          {isGenerating ? (
            <Box sx={{ width: '100%', mt: 4, position: 'relative' }}>
              <Typography variant="body1" gutterBottom>
                {statusMessage || 'Generating singing photo video... This may take around 3 minutes.'}
              </Typography>
              {errorMessage ? (
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    borderLeft: '4px solid',
                    borderColor: 'error.main',
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Error Occurred
                  </Typography>
                  <Typography variant="body1">{errorMessage}</Typography>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={handleReset}
                  >
                    Reset and Try Again
                  </Button>
                </Paper>
              ) : (
                <>
                  <LinearProgress
                    variant="determinate"
                    value={generationProgress}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      mb: 1,
                      '& .MuiLinearProgress-bar': { animation: 'pulse 1.5s infinite ease-in-out' },
                    }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="right"
                  >{`${Math.round(generationProgress)}%`}</Typography>
                </>
              )}
              <Box
                sx={{
                  mt: 3,
                  width: '100%',
                  height: '300px',
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  position: 'relative',
                }}
              >
                <Typography variant="h6" color="white" align="center">
                  {statusMessage || 'Processing your singing photo video...'}
                </Typography>
              </Box>
            </Box>
          ) : generatedVideo ? (
            <Box sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ flex: '0 0 auto' }}>
                <VideoPlayer videoUrl={generatedVideo} />
              </Box>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2, flex: '0 0 auto' }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="medium"
                  onClick={handleUpscaleVideo}
                  startIcon={<UpgradeIcon />}
                  sx={{ minWidth: 180 }}
                >
                  Upscale To 720p
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="medium"
                  onClick={handleSendToAddSound}
                  startIcon={<AudiotrackIcon />}
                  sx={{ minWidth: 260 }}
                >
                  Send to "Add Sound To Video" task
                </Button>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
              }}
            >
              <Typography variant="body1">
                No video generated yet. Start by uploading an image.
              </Typography>
            </Box>
          )}
        </StyledPaper>
      </Grid>
    </Grid>
  );
};

export default SingingPhotoGenerator;
